import {
  FSetupFetchActionType,
  SchemaFieldFurniture,
  SchemaFields,
} from '@spazious/dollhouse-fsetup';
import { Furniture, QueryResolvers, Resolvers } from '@spazious/dollhouse-graphql';
import { defaultSchemaFetchMethod } from '@spazious/dollhouse-mock';
import { decodeFCompositionId } from '@spazious/dollhouse-shallow-graph';
import { parseFCompositionType } from '@spazious/dollhouse-shared';
import { normalize } from '@spazious/jsonapi-tools';

import { ApolloContext } from '../context.js';
import { mapFurniture } from '../helpers/index.js';

const parseFurnitureOption = ({
  name,
  nid,
}: Furniture): SchemaFieldFurniture['options'][number] => ({
  label: name,
  value: nid,
});

/**
 * Resolver for the FComposition.
 */
export const FComposition: Resolvers<ApolloContext>['FComposition'] = {
  async schema(root, _, context) {
    const { api } = context;

    const { schema } = await defaultSchemaFetchMethod(
      FSetupFetchActionType.FCompositionSchema,
      root.nid
    );
    const { payload: configSchemaId } = decodeFCompositionId(root.nid);

    let furnitures: Furniture[] = [];
    let fields: SchemaFields = {};

    if (configSchemaId !== 0) {
      const result = await api.diagramming.retrieveDiagrammingDollhouseSchemasId({
        id: `${configSchemaId}`,
      });
      const data = normalize(result.data as any) as any;
      console.log('data', data.calculatedFurnitures);
      furnitures = ((data.calculatedFurnitures ?? []) as any[]).map(({ id, ...attributes }) =>
        mapFurniture({ id, attributes } as any)
      );
      fields = data.fields;
    }

    return {
      ...schema,
      fields: Object.entries(schema.fields).reduce((acc, [fieldName, field]) => {
        if (field.type === 'furniture') {
          const { furnitureTypes } = field;
          const options: SchemaFieldFurniture['options'] = furnitures.reduce((acc, furniture) => {
            if (furnitureTypes?.includes(furniture.objectType)) {
              return [...acc, parseFurnitureOption(furniture)];
            }
            return acc;
          }, [] as SchemaFieldFurniture['options']);

          if (!options.length) {
            throw new Error(
              `No furniture options available for this field "${fieldName}" "${furnitureTypes}"\n${JSON.stringify(
                schema,
                null,
                4
              )}`
            );
          }

          field = {
            ...field,
            options,
            default: options.length ? options[0].value : 0, // use first option as default
          };
        }

        const overrideField =
          fields[fieldName] ?? (null as SchemaFields[keyof SchemaFields] | null);

        if (overrideField && overrideField?.type !== field.type) {
          throw new Error(
            `Field type mismatch for field "${fieldName}" expected "${field.type}" got "${overrideField.type}"`
          );
        }

        return {
          ...acc,
          [fieldName]: {
            ...field,
            ...(overrideField ?? {}),
          } as SchemaFields[keyof SchemaFields],
        };
      }, {} as SchemaFields),
    };
  },
};

export const fcomposition: QueryResolvers<ApolloContext>['fcomposition'] = async (
  _,
  variables,
  context
) => {
  const { api } = context;
  const nid = parseInt(variables.id);
  const { type, payload } = decodeFCompositionId(parseInt(variables.id));
  const parsedType = parseFCompositionType(type);

  const {
    data: {
      data: { attributes },
    },
  } = await api.diagramming.retrieveDiagrammingDollhouseSchemasId({ id: `${payload}` });

  return {
    id: attributes!.uuid!.toString(),
    nid,
    name: attributes!.schemaType,
    type: parsedType,
    schema: null!,
  };
};
